/*
 * Payroll, time and My pay as React Query hooks (draft 4). Marking a week paid and undoing it refresh every week, since a
 * paid week sends its later changes to the next unpaid one.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type {
  ClockAnswer,
  ClockWhere,
  MyPayAccess,
  MyPayAnswer,
  MyPayList,
  PayPatch,
  PayPersonRow,
  PayWeekAnswer,
  ShiftEvent,
  ShiftInput,
  ShiftRow,
} from "@steward/shared";
import { api } from "./client";
import { qs } from "./hooks";

export const usePayWeek = (companyId: string, start?: string) =>
  useQuery({
    queryKey: ["payroll", companyId, start ?? "now"],
    queryFn: () => api<PayWeekAnswer>("GET", `payroll${qs({ start })}`),
    placeholderData: keepPreviousData,
  });

export function usePayRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { start: string; action: "paid" | "undo"; early?: boolean; noCrewOk?: boolean }) =>
      api<PayWeekAnswer>(
        "POST",
        `payroll/${p.start}/${p.action}`,
        p.action === "paid" ? { early: Boolean(p.early), noCrewOk: Boolean(p.noCrewOk) } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payroll"] });
      void qc.invalidateQueries({ queryKey: ["mypay"] });
    },
  });
}

export const usePayPeople = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["pay-people", companyId], queryFn: () => api<PayPersonRow[]>("GET", "payroll/people"), enabled });

export function useSetPay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; patch: PayPatch }) => api<PayPersonRow>("PATCH", `payroll/people/${p.id}`, p.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pay-people"] });
      void qc.invalidateQueries({ queryKey: ["payroll"] });
    },
  });
}

export const useShifts = (companyId: string, from: string, to: string) =>
  useQuery({
    queryKey: ["shifts", companyId, from, to],
    queryFn: () => api<ShiftRow[]>("GET", `shifts${qs({ from, to })}`),
    placeholderData: keepPreviousData,
  });

/** What someone did during a shift, for its activity log. */
export const useShiftActivity = (id: string) =>
  useQuery({
    queryKey: ["shifts", "activity", id],
    queryFn: () => api<ShiftEvent[]>("GET", `shifts/${id}/activity`),
  });

export function useSaveShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id?: string | undefined; body: ShiftInput }) =>
      p.id ? api<ShiftRow>("PATCH", `shifts/${p.id}`, p.body) : api<ShiftRow>("POST", "shifts", p.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["shifts"] });
      void qc.invalidateQueries({ queryKey: ["payroll"] });
    },
  });
}

export function useDeleteShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>("DELETE", `shifts/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["shifts"] });
      void qc.invalidateQueries({ queryKey: ["payroll"] });
    },
  });
}

export const useClock = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["clock", companyId], queryFn: () => api<ClockAnswer>("GET", "clock"), enabled, refetchInterval: 60_000 });

/**
 * Where the phone or computer is, when the person allows it (question 51). Never waits more than 5 seconds, and a "no" or
 * a slow answer clocks without a place.
 */
function whereNow(): Promise<ClockWhere | null> {
  if (__PREVIEW__ || typeof navigator === "undefined" || !("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((done) => {
    navigator.geolocation.getCurrentPosition(
      (p) =>
        done({
          lat: Number(p.coords.latitude.toFixed(6)),
          lng: Number(p.coords.longitude.toFixed(6)),
          accuracyM: Math.min(100_000, Math.round(p.coords.accuracy)),
        }),
      () => done(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60_000 },
    );
  });
}

/**
 * While this person is on the clock and the company keeps Live GPS on, sends where the phone is every 5 minutes for the
 * green pins on the Home map (as the phone app does). Off the clock nothing is sent, and the server keeps nothing.
 */
export function useLiveSpot(companyId: string, enabled: boolean) {
  const clock = useClock(companyId, enabled);
  const on = Boolean(enabled && clock.data?.on && clock.data.liveGps);
  useEffect(() => {
    if (!on) return;
    const send = () =>
      void whereNow().then((at) => (at ? api<{ kept: boolean }>("POST", "my-location", at).catch(() => null) : null));
    send();
    const timer = setInterval(send, 5 * 60_000);
    return () => clearInterval(timer);
  }, [on]);
}

export function usePressClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (to: "in" | "out") => api<ClockAnswer>("POST", `clock/${to}`, { where: await whereNow() }),
    onSuccess: (a) => {
      qc.setQueriesData({ queryKey: ["clock"] }, a);
      void qc.invalidateQueries({ queryKey: ["shifts"] });
    },
  });
}

/** Whether My pay shows for the signed-in person: on the team list, with their My pay switch on (questions 49, 50). */
export const useMyPayAccess = (companyId: string, enabled = true) =>
  useQuery({
    queryKey: ["mypay", companyId, "access"],
    queryFn: () => api<MyPayAccess>("GET", "my-pay-access"),
    enabled,
    staleTime: 60_000,
  });

export const useMyPay = (companyId: string) =>
  useQuery({ queryKey: ["mypay", companyId], queryFn: () => api<MyPayList>("GET", "my-pay") });
export const useMyPayWeek = (companyId: string, start: string | null) =>
  useQuery({
    queryKey: ["mypay", companyId, start],
    queryFn: () => api<MyPayAnswer>("GET", `my-pay/${start ?? ""}`),
    enabled: Boolean(start),
  });
